import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { AppHeader } from '../components/AppHeader';
import { Toast } from '../components/Toast';
import { useCreateAgenda, useAgendas } from '../api/queries';
import { getAccentColor, type Agenda, type MeetingType, type MeetingTypeConfig } from '../types/agenda';
import { useTenant } from '../auth/TenantProvider';
import { AccountMenu } from '../auth/AccountMenu';
import './new-agenda.css';

function nextWeekday(target: number): Date {
  // target: 1=Mon … 7=Sun (ISO). JS getDay(): 0=Sun…6=Sat.
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const iso = today.getDay() === 0 ? 7 : today.getDay();
  let ahead = target - iso;
  if (ahead <= 0) ahead += 7;
  today.setDate(today.getDate() + ahead);
  return today;
}

function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Apply template tokens to a title format string.
 * Supported tokens: {date:long}, {weekday}, {org}
 */
function applyTitleTemplate(
  format: string,
  date: Date,
  organization?: string,
): string {
  return format
    .replace('{date:long}', date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))
    .replace('{weekday}', date.toLocaleDateString('en-US', { weekday: 'long' }))
    .replace('{org}', organization || '');
}

interface FormState {
  title: string;
  date: string;
  location: string;
  meetingTime: string;
  organization: string;
  series: string;
}

function defaultsFor(config: MeetingTypeConfig): FormState {
  const next = nextWeekday(config.cadence.weekday);
  const d = fmtDate(next);
  const title = applyTitleTemplate(config.titleFormat, next, config.defaults.organization);
  return {
    title,
    date: d,
    location: config.defaults.location,
    meetingTime: config.cadence.defaultTime,
    organization: config.defaults.organization,
    series: config.defaults.series,
  };
}

export function NewAgendaScreen() {
  const navigate = useNavigate();
  const create = useCreateAgenda();
  const { data: listData } = useAgendas();
  const { activeTenant } = useTenant();
  const [type, setType] = useState<MeetingType | null>(null);
  const [form, setForm] = useState<FormState>(() => {
    if (activeTenant && activeTenant.meetingTypes.length > 0) {
      return defaultsFor(activeTenant.meetingTypes[0]);
    }
    // Fallback form when no tenant
    return {
      title: '',
      date: fmtDate(new Date()),
      location: '',
      meetingTime: '',
      organization: '',
      series: '',
    };
  });
  const [toast, setToast] = useState<string | null>(null);

  const getMeetingTypeConfig = (typeKey: MeetingType): MeetingTypeConfig | undefined => {
    return activeTenant?.meetingTypes.find((mt) => mt.key === typeKey);
  };

  const canCreate = (mt: MeetingType): boolean =>
    !listData?.permissions || listData.permissions.writeTypes.includes(mt);

  const choose = (typeKey: MeetingType) => {
    setType(typeKey);
    const config = getMeetingTypeConfig(typeKey);
    if (config) {
      const prev = listData?.agendas.find((a) => a.meetingType === typeKey);
      const base = defaultsFor(config);
      setForm({ ...base, location: prev?.location ?? base.location });
    }
  };

  const submit = async () => {
    if (!type || !form.title.trim() || !form.date.trim()) {
      setToast('Title and date are required');
      return;
    }
    const agenda: Agenda = {
      id: '',
      version: 0,
      meetingType: type,
      title: form.title.trim(),
      date: form.date.trim(),
      location: form.location.trim() || undefined,
      meetingTime: form.meetingTime.trim() || undefined,
      organization: form.organization.trim() || undefined,
      series: form.series.trim() || undefined,
      items: [],
      totalMinutes: 0,
    };
    try {
      const created = await create.mutateAsync(agenda);
      navigate(`/agenda/${created.id}/edit`, { replace: true });
    } catch (e) {
      setToast(`Error: ${(e as Error).message}`);
    }
  };

  const set = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const typeConfig = type ? getMeetingTypeConfig(type) : undefined;
  const showMeetingTimeField = typeConfig?.cadence.defaultTime !== '';
  const showSeriesField = typeConfig?.defaults.series !== '';

  return (
    <Screen
      header={
        <AppHeader
          title="New Agenda"
          onBack={() => (type ? setType(null) : navigate('/'))}
          actions={<AccountMenu />}
        />
      }
    >
      {type === null ? (
        <div className="type-picker">
          <h2 className="type-picker-q">What kind of meeting?</h2>
          {activeTenant && activeTenant.meetingTypes.map((mtConfig) => (
            <button
              key={mtConfig.key}
              className="type-card"
              style={{ '--c': getAccentColor(mtConfig.key, activeTenant) } as React.CSSProperties}
              disabled={!canCreate(mtConfig.key)}
              onClick={() => canCreate(mtConfig.key) && choose(mtConfig.key)}
            >
              <span className="type-card-icon">📋</span>
              <span className="type-card-title">{mtConfig.label}</span>
              <span className="type-card-desc">
                {mtConfig.itemTypes.map((it) => it.label).join(', ')} items
              </span>
              {!canCreate(mtConfig.key) && (
                <span className="type-card-locked">No permission</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="new-form">
          <label className="field">
            <span className="field-label">Title</span>
            <input className="input" value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </label>
          <div className="field-row">
            <label className="field">
              <span className="field-label">Date</span>
              <input className="input" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
            </label>
            {showMeetingTimeField && (
              <label className="field">
                <span className="field-label">Meeting time</span>
                <input className="input" value={form.meetingTime} onChange={(e) => set({ meetingTime: e.target.value })} placeholder={typeConfig?.cadence.defaultTime} />
              </label>
            )}
          </div>
          <label className="field">
            <span className="field-label">Location</span>
            <input className="input" value={form.location} onChange={(e) => set({ location: e.target.value })} />
          </label>
          {form.organization && (
            <label className="field">
              <span className="field-label">Organization</span>
              <input className="input" value={form.organization} onChange={(e) => set({ organization: e.target.value })} />
            </label>
          )}
          {showSeriesField && (
            <label className="field">
              <span className="field-label">Series</span>
              <input className="input" value={form.series} onChange={(e) => set({ series: e.target.value })} />
            </label>
          )}
          <button className="btn btn-primary btn-block" disabled={create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create Agenda'}
          </button>
        </div>
      )}
      <Toast message={toast} onDone={() => setToast(null)} />
    </Screen>
  );
}
